import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../contexts/AppContext';
import MermaidDiagram from '../../components/MermaidDiagram';
import ChatDialog from '../../components/ChatDialog';
import ProcessForm from '../../components/ProcessForm';
import NotificationCenter from '../../components/NotificationCenter';
import AlertModal from '../../components/AlertModal';
import AnnouncementModal from '../../components/AnnouncementModal';
import styles from './index.module.scss';

const Home = () => {
  const navigate = useNavigate();
  const chatDialogRef = useRef();
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [forceOpenInitialQuestionnaireSignal, setForceOpenInitialQuestionnaireSignal] = useState(0);

  // 从Context获取状态
  const {
    isChatVisible,
    setIsChatVisible,
    mermaidData,
    setMermaidData,
    currentChartData,
    setCurrentChartData,
    detectedProcesses,
    setDetectedProcesses,
    showForm,
    setShowForm,
    formCompleted,
    setFormCompleted,
    shouldResetView,
    setShouldResetView,
    resetHomeState,
    defaultMermaidData,
    alertModal,
    closeAlert
  } = useAppContext();

  // 检查是否首次访问，显示公告
  useEffect(() => {
    const hasSeenAnnouncement = localStorage.getItem('hasSeenAnnouncement_v2');
    if (!hasSeenAnnouncement) {
      setShowAnnouncement(true);
    }
  }, []);

  // 进入页面默认打开 AI 对话
  useEffect(() => {
    setIsChatVisible(true);
  }, [setIsChatVisible]);

  // 关闭公告并记录已查看
  const handleCloseAnnouncement = () => {
    setShowAnnouncement(false);
    localStorage.setItem('hasSeenAnnouncement_v2', 'true');
  };

  // 只切换对话框的显示状态，不重置对话内容
  const toggleChat = () => {
    setIsChatVisible(!isChatVisible);
  };

  // 切换表单显示状态
  const toggleForm = () => {
    if (!formCompleted) {
      setShowForm(!showForm);
    }
  };

  // 发送当前流程图数据到AI对话
  const handleSendCurrentChart = () => {
    if (chatDialogRef.current && currentChartData) {
      setIsChatVisible(true);
      chatDialogRef.current.sendMessage(`\`\`\`mermaid\n${currentChartData}\n\`\`\``);
    }
  };

  // 处理从AI返回的流程图数据
  const handleMermaidData = (data, reset = false) => {
    if (data) {
      if (data.trim() === mermaidData.trim() && !reset) {
        setMermaidData(data.trim() + `\n%% Rerender: ${Date.now()}`);
      } else {
        setMermaidData(data.trim());
      }
      setShouldResetView(reset);
    }
  };

  // 处理图表数据更新
  const handleChartDataChange = (data) => {
    setCurrentChartData(data);
  };

  // 处理AI返回的工艺流程数组
  const handleProcessesDetected = (processes) => {
    if (Array.isArray(processes) && processes.length > 0) {
      setDetectedProcesses(processes);
      setShowForm(true);
      setFormCompleted(false);
    }
  };

  // 处理表单完成
  const handleFormComplete = (results) => {
    console.log('表单结果:', results);
    setFormCompleted(true);
    // 0.5秒后关闭表单并重置状态
    setTimeout(() => {
      setShowForm(false);
      setDetectedProcesses([]);
    }, 500);
  };

  // 重置对话
  const handleResetChat = () => {
    chatDialogRef.current?.resetChat();
    resetHomeState();
  };

  // 处理跳转到上传新工艺页面
  const handleUploadProcess = () => {
    navigate('/upload-process');
  };

  // 处理跳转到审核节点页面
  const handleAuditNode = () => {
    navigate('/audit-node');
  };

  const handleInitialHintClick = () => {
    setIsChatVisible(true);
    setForceOpenInitialQuestionnaireSignal((v) => v + 1);
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            <h1>北航实验室仪器制备方案</h1>
            <h2>北航器件制备工艺智能助手</h2>
          </div>
          <div className={styles.headerButtons}>
            <button
              className={`${styles.headerButton} ${styles.helpButton}`}
              onClick={() => setShowAnnouncement(true)}
            >
              💡 功能介绍
            </button>
            <button
              className={`${styles.headerButton} ${styles.uploadButton}`}
              onClick={handleUploadProcess}
            >
              上传新工艺
            </button>
            <button
              className={`${styles.headerButton} ${styles.auditButton}`}
              onClick={handleAuditNode}
            >
              审核节点
            </button>
          </div>
        </div>
      </header>
      
      <div className={styles.content}>
        <div className={styles.diagramContainer}>
          <MermaidDiagram 
            chart={mermaidData} 
            shouldResetView={shouldResetView}
            onViewReset={() => setShouldResetView(false)}
            onChartDataChange={handleChartDataChange}
            onInitialHintClick={handleInitialHintClick}
          />
        </div>
      </div>

      <footer className={styles.footer}>
        <div className={styles.footerButtons}>
          {/* <button 
            className={`${styles.footerButton} ${styles.sendButton}`}
            onClick={handleSendCurrentChart}
          >
            发送当前方案
          </button> */}
          <button 
            className={`${styles.footerButton} ${styles.resetButton}`}
            onClick={handleResetChat}
          >
            开始新对话
          </button>
          <button 
            className={`${styles.footerButton} ${styles.aiButton}`}
            onClick={toggleChat}
          >
            AI对话
          </button>
        </div>
        <div className={styles.footerContent}>
          <span className={styles.copyright}>© 2025 北京航空航天大学 - 实验室仪器制备方案</span>
        </div>
      </footer>

      <ChatDialog 
        ref={chatDialogRef}
        visible={isChatVisible}
        onClose={toggleChat}
        onMermaidData={handleMermaidData}
        onProcessesDetected={handleProcessesDetected}
        autoOpenInitialQuestionnaire
        forceOpenInitialQuestionnaireSignal={forceOpenInitialQuestionnaireSignal}
      />

      {detectedProcesses.length > 0 && (
        <>
          <ProcessForm 
            processes={detectedProcesses}
            onComplete={handleFormComplete}
            visible={showForm}
            onClose={toggleForm}
            chatDialogRef={chatDialogRef}
          />
          {!showForm && !formCompleted && (
            <button className={styles.formTrigger} onClick={toggleForm}>
              <img src="/form.svg" alt="打开表单" />
            </button>
          )}
        </>
      )}

      {/* 通知中心 */}
      <NotificationCenter />

      {/* 弹窗组件 */}
      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        confirmText={alertModal.confirmText}
        cancelText={alertModal.cancelText}
        showCancel={alertModal.showCancel}
        onConfirm={alertModal.onConfirm}
        onCancel={alertModal.onCancel}
        onClose={closeAlert}
      />

      {/* 功能公告 */}
      <AnnouncementModal
        isOpen={showAnnouncement}
        onClose={handleCloseAnnouncement}
      />
    </div>
  );
};

export default Home;